import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import CreateSignupApi from '../../api/createSignup.api';
import {getBearerHeader} from '../../helpers/api.helper';
import {validateFile} from '../../helpers/invoiceUpload.helper';
import {scanIdForCurrentDocuments} from '../../helpers/createSignup.helper';
import {GoogleAnalyticsHelper} from '../../helpers/googleAnalytics.helper';
import {
  ANALYTICS_EVENTS,
  EMAIL_REGEX,
  STRONG_MATCH_THRESHOLD,
  CUSTOMER_FIELDS,
  SIGNUP_CUSTOMER_TYPE_GROUPS,
  SIGNUP_CUSTOMER_TYPE_NAME_BY_ID,
  SIGNUP_PHOTO_CAPTURE_CONSTRAINTS,
  SIGNUP_PHOTO_MAX_DIMENSION,
  SIGNUP_PHOTO_JPEG_QUALITY,
  SIGNUP_MATCHED_ON_LABELS,
  SIGNUP_STAFF_STEP_LABELS,
  SIGNUP_NON_STAFF_STEP_LABELS,
  SIGNUP_TERMINAL_STATUSES,
  REFERRER_POLL_DONE_STATUSES,
  REFERRER_POLL_INTERVAL_MS,
  REFERRER_POLL_MAX_ATTEMPTS,
  REFERRAL_SCAN_POLL_INTERVAL_MS,
  REFERRAL_SCAN_POLL_MAX_ATTEMPTS,
  SERVICE_TYPE,
  INVOICE_UPLOAD_ACCEPTED_TYPES,
  MESSAGES,
} from '../../constants/constants';
import Loading from '../../components/Loading/Loading';
import PriceComparisonPanel from '../../components/PriceComparisonPanel/PriceComparisonPanel';
import {
  PageWrapper,
  PageTitle,
  StepIndicatorWrapper,
  StepItem,
  StepDot,
  StepLabel,
  StepLine,
  StepCard,
  SectionTitle,
  DropZone,
  DropZoneLabel,
  DropZoneFormats,
  DocThumb,
  FulfillmentChoice,
  FulfillmentChoiceLabel,
  FulfillmentChoiceHint,
  FulfillmentOptions,
  FulfillmentChoiceOption,
  PhotoGrid,
  PhotoCard,
  PhotoThumb,
  DeleteButton,
  FormGroup,
  Label,
  Input,
  FieldError,
  FieldWarning,
  ResolvedFieldsGrid,
  ResolvedFieldCard,
  FieldLabel,
  FieldValueRow,
  FieldValue,
  FieldNullValue,
  EditButton,
  InlineEditRow,
  SaveButton,
  CancelEditButton,
  ProcessingWrapper,
  ProcessingTitle,
  ProcessingStatusText,
  SpinnerWrapper,
  StatusBadge,
  ErrorMessage,
  DuplicateWarning,
  PossibleAccountNotice,
  DuplicateMatch,
  SuccessCard,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
  LoadingOverlay,
  CenteredSection,
  CameraOverlay,
  VideoPreview,
  CaptureButton,
  CameraControls,
  SectionHint,
  PhotoPreviewOverlay,
} from './CreateSignupPage.style';

const strongMatches = signup =>
  ((signup && signup.existingAccountMatches) || []).filter(
    m => m.score >= STRONG_MATCH_THRESHOLD
  );

const INTERMEDIATE_STATUS_LABELS = {
  CREATED: 'Signup created',
  DOCUMENT_UPLOADED: 'Documents received',
  EXTRACTING: 'Extracting document data…',
  EXTRACTED: 'Data extracted',
  VERIFYING: 'Verifying business details…',
  SUBMITTING: 'Submitting to onboarding…',
};

const RESOLVED_FIELD_LABELS = {
  businessName: 'Business Name',
  tradingName: 'Trading Name',
  vatNumber: 'VAT Number',
  companyNumber: 'Company Number',
  streetAddress: 'Street Address',
  city: 'City',
  postalCode: 'Postcode',
  phoneNumber: 'Phone Number',
  email: 'Email',
  website: 'Website',
  contactName: 'Contact Name',
  customerType: 'Customer Type',
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

const StepIndicator = ({currentStep, labels}) => (
  <StepIndicatorWrapper>
    {labels.map((label, i) => (
      <React.Fragment key={i}>
        <StepItem>
          <StepDot $active={currentStep === i} $done={currentStep > i}>
            {currentStep > i ? '✓' : i + 1}
          </StepDot>
          <StepLabel $active={currentStep === i}>{label}</StepLabel>
        </StepItem>
        {i < labels.length - 1 && <StepLine $done={currentStep > i} />}
      </React.Fragment>
    ))}
  </StepIndicatorWrapper>
);

StepIndicator.propTypes = {
  currentStep: PropTypes.number.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
};

// ─── Resolved Field Row ───────────────────────────────────────────────────────

const ResolvedField = ({
  fieldName,
  fieldData,
  onSave,
  saving,
  saveError,
  canEdit,
}) => {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState('');
  const inputRef = useRef(null);

  // Customer type must be one of the D365 codes, so it's edited via a dropdown
  // rather than free text — free-text values are rejected at registration.
  const isCustomerType = fieldName === 'customerType';

  const startEdit = () => {
    const value = fieldData ? fieldData.value || '' : '';
    // A legacy free-text value won't match any code — start unselected.
    setEditVal(
      isCustomerType && !SIGNUP_CUSTOMER_TYPE_NAME_BY_ID[value] ? '' : value
    );
    setEditing(true);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditVal('');
  };

  const canSave = !isCustomerType || !!editVal;

  const handleSave = async () => {
    if (!canSave) return;
    await onSave(fieldName, editVal);
    setEditing(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') cancelEdit();
  };

  const label = RESOLVED_FIELD_LABELS[fieldName] || fieldName;
  const currentValue = fieldData ? fieldData.value : null;
  const displayValue =
    isCustomerType && currentValue != null
      ? SIGNUP_CUSTOMER_TYPE_NAME_BY_ID[currentValue] || currentValue
      : currentValue;

  return (
    <ResolvedFieldCard>
      <FieldLabel>{label}</FieldLabel>
      {editing ? (
        <>
          <InlineEditRow>
            {isCustomerType ? (
              <select
                ref={inputRef}
                value={editVal}
                onChange={e => setEditVal(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={saving}
              >
                <option value="" disabled>
                  Select customer type…
                </option>
                {SIGNUP_CUSTOMER_TYPE_GROUPS.map(g => (
                  <optgroup key={g.group} label={g.group}>
                    {g.types.map(ct => (
                      <option key={ct.id} value={ct.id}>
                        {ct.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            ) : (
              <input
                ref={inputRef}
                value={editVal}
                onChange={e => setEditVal(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={saving}
              />
            )}
            <SaveButton onClick={handleSave} disabled={saving || !canSave}>
              {saving ? '…' : 'Save'}
            </SaveButton>
            <CancelEditButton onClick={cancelEdit} disabled={saving}>
              ✕
            </CancelEditButton>
          </InlineEditRow>
          {saveError && <FieldError>{saveError}</FieldError>}
        </>
      ) : (
        <FieldValueRow>
          {displayValue != null ? (
            <FieldValue>{displayValue}</FieldValue>
          ) : (
            <FieldNullValue>Not available</FieldNullValue>
          )}
          {canEdit && <EditButton onClick={startEdit}>Edit</EditButton>}
        </FieldValueRow>
      )}
    </ResolvedFieldCard>
  );
};

ResolvedField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  fieldData: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  saveError: PropTypes.string,
  canEdit: PropTypes.bool,
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const CreateSignupPage = ({history}) => {
  const auth = useSelector(state => state.auth);
  const authHeaders = getBearerHeader(null, auth);
  const isStaffAccount = useSelector(state => state.isStaffAccount);
  // The price comparison is account-signup-svc's own invoice-scanning upload of
  // this signup's documents (learnt from the signup, see the poll below) — never a
  // second upload of our own, which raced it in duplicate detection and could
  // leave the referral's scan flagged DUPLICATE and earning no commission.
  const [priceComparisonId, setPriceComparisonId] = useState(null);
  const [priceComparisonUnavailable, setPriceComparisonUnavailable] = useState(
    false
  );

  // Location + init
  const [locationStatus, setLocationStatus] = useState('idle');
  const [location, setLocation] = useState(null);
  const [signupId, setSignupId] = useState(null);

  // Step navigation
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1 — Upload
  const [pendingPhotos, setPendingPhotos] = useState([]); // staged locally, not yet uploaded
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('idle');
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraMirrored, setCameraMirrored] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  // No default — the rep must actively pick one (see handleNextFromStep1),
  // rather than silently comparing against a delivery price the customer
  // never asked for.
  const [fulfillmentType, setFulfillmentType] = useState(null);
  const [fulfillmentTypeError, setFulfillmentTypeError] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  // Synchronous re-entrancy guards for async submit handlers — the `disabled`
  // prop on their buttons is driven by state, which doesn't update until the
  // next render, so a second click/tap in the same tick would pass it. These
  // refs update immediately and stop that.
  const uploadingRef = useRef(false);
  const confirmingAccountRef = useRef(false);

  // Step 2 — Contact Infomation + resolved data
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [confirmEmailError, setConfirmEmailError] = useState(null);
  const [mobileError, setMobileError] = useState(null);
  const [contactError, setContactError] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const [resolvedData, setResolvedData] = useState(null);
  const [blockingMatches, setBlockingMatches] = useState([]);
  const [fieldSaving, setFieldSaving] = useState({});
  const [fieldSaveErrors, setFieldSaveErrors] = useState({});

  // Step 3 — Processing
  const [processingStatus, setProcessingStatus] = useState('idle');
  const [processingError, setProcessingError] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitConflict, setSubmitConflict] = useState(null);
  // Staff's answer to "would you like us to create your account?" (no
  // existing account detected) — null until answered.
  const [accountCreationChoice, setAccountCreationChoice] = useState(null);
  // Staff's answer to "would you like us to contact you to discuss this
  // further?" (existing account detected) — null until answered.
  const [followUpChoice, setFollowUpChoice] = useState(null);
  // Non-staff only: the business may already have a JJ account. The backend
  // gives them just this flag — never which account, its name or address.
  const [possibleExistingAccount, setPossibleExistingAccount] = useState(false);

  // ─── Auth guard ─────────────────────────────────────────────────────────────

  const isLoggedIn = !!authHeaders;

  // ─── Location helpers ────────────────────────────────────────────────────────

  // The signup is created lazily — on the Contact step's "Next" — because
  // referringEmployeeId/employeeName (the driver's own Employee ID and name,
  // entered on that step) must be sent at creation time, not attached
  // afterwards.
  const initSignup = useCallback(
    async (lat, lng, referringEmployeeId, employeeName) => {
      try {
        const result = await CreateSignupApi.initSignup(
          lat,
          lng,
          referringEmployeeId,
          employeeName,
          authHeaders
        );
        setSignupId(result.id);
        return result.id;
      } catch (err) {
        setContactError(
          (err.response && err.response.data && err.response.data.message) ||
            err.message ||
            'Failed to start signup session. Please try again.'
        );
        return null;
      }
    },
    [authHeaders]
  );

  // Best-effort — the signup proceeds even if location can't be obtained
  // (denied, unavailable, or the device/browser just can't get a fix);
  // initSignup below already sends null lat/lng in that case.
  const requestLocation = useCallback(() => {
    if (!isLoggedIn) return;
    setLocationStatus('requesting');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setLocation({lat: pos.coords.latitude, lng: pos.coords.longitude});
        setLocationStatus('granted');
      },
      () => {
        setLocationStatus('denied');
      },
      {timeout: 10000}
    );
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && locationStatus === 'idle') {
      requestLocation();
    }
  }, [isLoggedIn]); // intentionally omit requestLocation — calling once on login is correct

  // ─── Step 1: Photo capture ────────────────────────────────────────────────────

  const stagePhoto = useCallback(files => {
    const errors = [];
    const valid = [];
    Array.from(files).forEach(f => {
      const err = validateFile(f);
      if (err) {
        errors.push(err);
      } else {
        valid.push(f);
      }
    });
    setUploadError(errors.length > 0 ? errors.join(' ') : null);
    if (valid.length > 0) {
      setPendingPhotos(prev => [
        ...prev,
        ...valid.map(f => ({file: f, preview: URL.createObjectURL(f)})),
      ]);
    }
  }, []);

  const removePendingPhoto = useCallback(index => {
    setPendingPhotos(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragOver(false);
      stagePhoto(e.dataTransfer.files);
    },
    [stagePhoto]
  );

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragOver(false), []);

  const handleFileInput = useCallback(
    e => {
      stagePhoto(e.target.files);
      e.target.value = '';
    },
    [stagePhoto]
  );

  // Doc ids currently mid-delete — only gate against uploadLoading here since
  // that flag tracks the upload/next-step request, not this one, and a rapid
  // double click on the same delete button would otherwise fire two DELETEs.
  const deletingDocIdsRef = useRef(new Set());

  const handleDeleteDoc = useCallback(
    async (docId, docName) => {
      if (deletingDocIdsRef.current.has(docId)) return;
      deletingDocIdsRef.current.add(docId);
      try {
        await CreateSignupApi.deleteDocument(signupId, docId, authHeaders);
        setUploadedDocs(prev => {
          const removed = prev.find(d => d.id === docId);
          if (removed && removed.preview) URL.revokeObjectURL(removed.preview);
          return prev.filter(d => d.id !== docId);
        });
      } catch (err) {
        setUploadError(`Failed to remove "${docName}". Please try again.`);
      } finally {
        deletingDocIdsRef.current.delete(docId);
      }
    },
    [signupId, authHeaders]
  );

  const handleNextFromStep1 = useCallback(async () => {
    setFulfillmentTypeError(null);
    if (!fulfillmentType) {
      setFulfillmentTypeError(MESSAGES.FULFILLMENT_TYPE_REQUIRED);
      return;
    }
    if (pendingPhotos.length === 0 && uploadedDocs.length === 0) return;

    if (pendingPhotos.length > 0) {
      // setUploadLoading/disabled only take effect next render, which a second
      // click/tap in the same tick can land before — this ref blocks it immediately.
      if (uploadingRef.current) return;
      uploadingRef.current = true;
      setUploadLoading(true);
      setUploadError(null);
      GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.CREATE_SIGNUP_UPLOAD, {
        category: 'Create Signup',
        fileCount: pendingPhotos.length,
      });
      try {
        const files = pendingPhotos.map(p => p.file);
        const result = await CreateSignupApi.uploadDocuments(
          signupId,
          files,
          location,
          fulfillmentType,
          authHeaders
        );
        const newDocs = (result.documentIds || []).map((id, i) => ({
          id,
          name: `Photo ${uploadedDocs.length + i + 1}`,
          preview: pendingPhotos[i] ? pendingPhotos[i].preview : null,
          isPdf:
            !!pendingPhotos[i] &&
            pendingPhotos[i].file.type === 'application/pdf',
        }));
        setUploadedDocs(prev => [...prev, ...newDocs]);
        setPendingPhotos([]);
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.CREATE_SIGNUP_UPLOAD_SUCCESS,
          {category: 'Create Signup'}
        );
      } catch (err) {
        const message =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Upload failed. Please try again.';
        setUploadError(message);
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.CREATE_SIGNUP_UPLOAD_FAILED,
          {category: 'Create Signup', error: message}
        );
        setUploadLoading(false);
        uploadingRef.current = false;
        return;
      }
      setUploadLoading(false);
      uploadingRef.current = false;
    }

    // Staff review/confirm the extracted details next; non-staff can't submit
    // anyway (only staff/admin may — see refer-a-business-openapi.yaml), so
    // they skip that step entirely and go straight to Price Comparison.
    setCurrentStep(isStaffAccount ? 2 : 3);
  }, [
    pendingPhotos,
    uploadedDocs.length,
    signupId,
    location,
    authHeaders,
    fulfillmentType,
    isStaffAccount,
  ]);

  // ─── Step 1: Camera ───────────────────────────────────────────────────────────

  // Attach stream to video element whenever the camera overlay opens
  useEffect(() => {
    if (cameraOpen && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen, cameraStream]);

  // Stop camera tracks when stream changes or component unmounts
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(t => t.stop());
      }
    };
  }, [cameraStream]);

  const openCamera = useCallback(async () => {
    // getUserMedia unavailable (HTTP, old browser) — fall back to file picker
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      fileInputRef.current && fileInputRef.current.click();
      return;
    }
    setCameraPermission('requesting');
    setUploadError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: {ideal: 'environment'},
          ...SIGNUP_PHOTO_CAPTURE_CONSTRAINTS,
        },
        audio: false,
      });
      setCameraStream(stream);
      setCameraPermission('granted');
      setCameraOpen(true);
    } catch (err) {
      if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        setCameraPermission('denied');
      } else {
        // Device/hardware error — fall back to file picker
        setCameraPermission('idle');
        fileInputRef.current && fileInputRef.current.click();
      }
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    setCameraOpen(false);
    setCameraPermission('idle');
  }, [cameraStream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const scale = Math.min(
      1,
      SIGNUP_PHOTO_MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight)
    );
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext('2d');
    if (cameraMirrored) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      blob => {
        if (blob) {
          const file = new File([blob], `photo-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          setPendingPhotos(prev => [
            ...prev,
            {file, preview: URL.createObjectURL(blob)},
          ]);
        }
        closeCamera();
      },
      'image/jpeg',
      SIGNUP_PHOTO_JPEG_QUALITY
    );
  }, [closeCamera, cameraMirrored]);

  // ─── Step 2: Contact Infomation + field editing ──────────────────────────────────────

  const saveContactField = useCallback(
    async (id, fieldName, value, setError) => {
      if (!value.trim()) return true;
      try {
        const updated = await CreateSignupApi.updateField(
          id,
          fieldName,
          value.trim(),
          authHeaders
        );
        if (updated && updated.resolvedData) {
          setResolvedData(updated.resolvedData);
        }
        if (updated) setBlockingMatches(strongMatches(updated));
        return true;
      } catch (err) {
        if (err.response && err.response.status === 409) {
          setError(
            `This ${
              fieldName === 'email' ? 'email address' : 'phone number'
            } is already registered. Please use a different value.`
          );
        } else {
          setError(
            (err.response && err.response.data && err.response.data.message) ||
              err.message ||
              'Failed to save. Please try again.'
          );
        }
        return false;
      }
    },
    [authHeaders]
  );

  // Contact details is now the first step: validate + persist the entered
  // details, then advance to the document upload step.
  const handleContactNext = useCallback(async () => {
    setEmailError(null);
    setConfirmEmailError(null);
    setMobileError(null);
    setContactError(null);

    // Validate email + confirm email before hitting the API
    const emailVal = email.trim();
    const confirmVal = confirmEmail.trim();

    if (emailVal && !EMAIL_REGEX.test(emailVal)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!confirmVal) {
      setConfirmEmailError('Please confirm the customer email address.');
      return;
    }
    if (!EMAIL_REGEX.test(confirmVal)) {
      setConfirmEmailError('Please enter a valid email address.');
      return;
    }
    if (emailVal !== confirmVal) {
      setConfirmEmailError('Email addresses do not match.');
      return;
    }
    if (!mobile.trim()) {
      setMobileError('Please enter the customer mobile number.');
      return;
    }
    setSavingContact(true);

    // The signup is created here (not eagerly on mount) so
    // referringEmployeeId/employeeName — the driver's own Employee ID and
    // name, just entered above — can be sent at creation time, as the API
    // requires.
    let activeSignupId = signupId;
    if (!activeSignupId) {
      activeSignupId = await initSignup(
        location && location.lat,
        location && location.lng,
        employeeId.trim() || undefined,
        employeeName.trim() || undefined
      );
      if (!activeSignupId) {
        setSavingContact(false);
        return;
      }
    }

    let ok = true;
    if (emailVal) {
      ok = await saveContactField(
        activeSignupId,
        'email',
        emailVal,
        setEmailError
      );
    }
    if (ok) {
      ok = await saveContactField(
        activeSignupId,
        'phoneNumber',
        mobile,
        setMobileError
      );
    }

    setSavingContact(false);
    if (ok) setCurrentStep(1);
  }, [
    email,
    confirmEmail,
    mobile,
    employeeId,
    employeeName,
    isStaffAccount,
    signupId,
    location,
    initSignup,
    saveContactField,
  ]);

  const handleFieldSave = useCallback(
    async (fieldName, value) => {
      setFieldSaving(prev => ({...prev, [fieldName]: true}));
      setFieldSaveErrors(prev => ({...prev, [fieldName]: null}));
      try {
        const updated = await CreateSignupApi.updateField(
          signupId,
          fieldName,
          value,
          authHeaders
        );
        if (updated && updated.resolvedData) {
          setResolvedData(updated.resolvedData);
        }
        if (updated) setBlockingMatches(strongMatches(updated));
      } catch (err) {
        const data = err.response && err.response.data;
        // Editing a field into a duplicate returns a 409. If it carries the existing
        // account, show the same rich hard block as submit (this account already exists
        // and can't be created from here) rather than a bare "already in use" message.
        if (
          err.response &&
          err.response.status === 409 &&
          data &&
          data.existingAccount
        ) {
          setSubmitConflict(data);
          return;
        }
        const msg =
          (data && data.message) ||
          (err.response && err.response.status === 409
            ? 'This value is already in use. Please provide a different one.'
            : err.message) ||
          'Save failed.';
        setFieldSaveErrors(prev => ({...prev, [fieldName]: msg}));
      } finally {
        setFieldSaving(prev => ({...prev, [fieldName]: false}));
      }
    },
    [signupId, authHeaders]
  );

  // ─── Step 3: Polling ──────────────────────────────────────────────────────────

  // Only staff review the extraction (on the Confirm Details step) and
  // submit — everyone else can't submit anyway, so there's nothing for them
  // to poll towards; the upload response alone is enough for their flow.
  useEffect(() => {
    if (currentStep !== 2 || !signupId || !authHeaders || !isStaffAccount) {
      return;
    }

    let stopped = false;
    let timeoutId;

    setProcessingStatus('polling');
    setProcessingError(null);

    const poll = async () => {
      if (stopped) return;
      try {
        const data = await CreateSignupApi.getSignup(signupId, authHeaders);
        if (stopped) return;
        // A prior poll may have set this on a transient failure — clear it
        // now that a poll has actually succeeded, so a stale message doesn't
        // keep showing alongside the (now recovered) status.
        setProcessingError(null);
        setProcessingStatus(data.status);

        if (data.status === 'VERIFIED') {
          setResolvedData(data.resolvedData || {});
          setBlockingMatches(strongMatches(data));
          // Pre-fill email/mobile if not yet set
          if (!email && data.resolvedData && data.resolvedData.email) {
            setEmail(data.resolvedData.email.value || '');
          }
          if (!mobile && data.resolvedData && data.resolvedData.phoneNumber) {
            setMobile(data.resolvedData.phoneNumber.value || '');
          }
          return;
        }

        if (data.status === 'FAILED') {
          setProcessingError(data.errorMessage || 'Verification failed.');
          return;
        }

        if (data.status === 'ACCOUNT_CREATED') {
          return;
        }

        // Intermediate — keep polling
        timeoutId = setTimeout(poll, 3000);
      } catch (err) {
        if (!stopped) {
          // A cancelled request (e.g. the tab was backgrounded, per the
          // app's idle-mode handling) isn't a real failure — retry almost
          // immediately instead of backing off, and don't alarm the user
          // with an error they'd otherwise have to wait out.
          if (err.requestInterrupted) {
            timeoutId = setTimeout(poll, 500);
          } else {
            setProcessingError(
              (err.response &&
                err.response.data &&
                err.response.data.message) ||
                err.message ||
                'Failed to retrieve status.'
            );
            // Retry after a longer delay on error
            timeoutId = setTimeout(poll, 6000);
          }
        }
      }
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [currentStep, signupId, isStaffAccount]); // authHeaders stable within session

  // Non-staff: poll quietly from Price Comparison onwards (currentStep 3 —
  // they skip 2 entirely) until verification has run, just to learn
  // possibleExistingAccount. Best-effort — the notice is a nicety, so
  // failures are retried silently, never shown.
  const pollForExistingAccount =
    currentStep >= 2 && !!signupId && !!authHeaders && !isStaffAccount;
  useEffect(() => {
    if (!pollForExistingAccount) return;

    let stopped = false;
    let timeoutId;
    let attempts = 0;

    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const data = await CreateSignupApi.getSignup(signupId, authHeaders);
        if (stopped) return;
        if (data.possibleExistingAccount) {
          setPossibleExistingAccount(true);
          return;
        }
        if (REFERRER_POLL_DONE_STATUSES.indexOf(data.status) !== -1) return;
      } catch (err) {
        // Retried below.
      }
      if (!stopped && attempts < REFERRER_POLL_MAX_ATTEMPTS) {
        timeoutId = setTimeout(poll, REFERRER_POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [pollForExistingAccount, signupId]); // authHeaders stable within session

  // Learn the price comparison's scan id from the signup. account-signup-svc sends
  // every current document to invoice-scanning as one invoice shortly after the
  // upload, and rebuilds it under a new id whenever the set of documents changes —
  // so only an id that covers every current document is the right one. Restarts
  // whenever a document is added or removed.
  const pollForPriceComparison =
    currentStep >= 2 && !!signupId && !!authHeaders && uploadedDocs.length > 0;
  useEffect(() => {
    if (!pollForPriceComparison) return undefined;

    let stopped = false;
    let timeoutId;
    let attempts = 0;
    setPriceComparisonId(null);
    setPriceComparisonUnavailable(false);

    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const data = await CreateSignupApi.getSignup(signupId, authHeaders);
        if (stopped) return;
        const processId = scanIdForCurrentDocuments(data);
        if (processId) {
          setPriceComparisonId(processId);
          return;
        }
        // Not an invoice (the speculative scan is withdrawn), or the signup has
        // finished processing without a scan — nothing will appear.
        if (
          data.documentIsInvoice === false ||
          SIGNUP_TERMINAL_STATUSES.indexOf(data.status) !== -1
        ) {
          setPriceComparisonUnavailable(true);
          return;
        }
      } catch (err) {
        // Retried below.
      }
      if (stopped) return;
      if (attempts < REFERRAL_SCAN_POLL_MAX_ATTEMPTS) {
        timeoutId = setTimeout(poll, REFERRAL_SCAN_POLL_INTERVAL_MS);
      } else {
        setPriceComparisonUnavailable(true);
      }
    };

    poll();
    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [pollForPriceComparison, signupId, uploadedDocs.length]); // authHeaders stable within session

  const renderPossibleExistingAccountNotice = () =>
    possibleExistingAccount && (
      <PossibleAccountNotice role="status">
        <strong>This business may already have an account</strong>
        It looks like this business may already be a JJ Foodservice customer.
        Our team will check before creating a new account &mdash; if it is, they
        can carry on ordering with their existing login.
      </PossibleAccountNotice>
    );

  // ─── Submit ───────────────────────────────────────────────────────────────────

  // Persists any unsaved contact-field edits and moves to the final step —
  // account creation itself is no longer triggered here. It now waits for the
  // staff member to relay the customer's answer to "would you like us to
  // create your account?" on that screen (see handleConfirmAccountCreation).
  const handleContinueToFinalStep = useCallback(async () => {
    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitConflict(null);

    // Persist any edits made to the contact fields on this screen before
    // continuing — submit reads the saved signup, not the inputs, so an
    // unsaved correction here would otherwise be silently ignored.
    const emailVal = email.trim();
    const mobileVal = mobile.trim();
    const resolvedEmail =
      (resolvedData && resolvedData.email && resolvedData.email.value) || '';
    const resolvedPhone =
      (resolvedData &&
        resolvedData.phoneNumber &&
        resolvedData.phoneNumber.value) ||
      '';

    if (emailVal && emailVal !== resolvedEmail) {
      if (emailVal !== confirmEmail.trim()) {
        setConfirmEmailError('Email addresses do not match.');
        setSubmitLoading(false);
        return;
      }
      const ok = await saveContactField(
        signupId,
        'email',
        emailVal,
        setEmailError
      );
      if (!ok) {
        setSubmitLoading(false);
        return;
      }
    }
    if (mobileVal && mobileVal !== resolvedPhone) {
      const ok = await saveContactField(
        signupId,
        'phoneNumber',
        mobileVal,
        setMobileError
      );
      if (!ok) {
        setSubmitLoading(false);
        return;
      }
    }

    setSubmitLoading(false);
    setCurrentStep(3);
  }, [signupId, email, confirmEmail, mobile, resolvedData, saveContactField]);

  // Fired when the customer says "yes" to "would you like us to create your
  // account?" — this is the actual account-creation trigger, now deferred
  // until this explicit confirmation instead of firing automatically.
  const handleConfirmAccountCreation = useCallback(async () => {
    // This button has no `disabled` of its own — submitLoading only gates the
    // whole card's re-render, which lags a tick behind a rapid double click/tap.
    if (confirmingAccountRef.current) return;
    confirmingAccountRef.current = true;
    setAccountCreationChoice('yes');
    setSubmitLoading(true);
    setSubmitError(null);
    setSubmitConflict(null);
    GoogleAnalyticsHelper.trackEvent(ANALYTICS_EVENTS.CREATE_SIGNUP_SUBMIT, {
      category: 'Create Signup',
    });
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.CREATE_SIGNUP_ACCOUNT_CREATION_ACCEPTED,
      {category: 'Create Signup'}
    );

    try {
      const result = await CreateSignupApi.submitSignup(signupId, authHeaders);
      if (result.status === 'ACCOUNT_CREATED') {
        setProcessingStatus('ACCOUNT_CREATED');
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.CREATE_SIGNUP_SUBMIT_SUCCESS,
          {category: 'Create Signup', tradingAccountId: result.tradingAccountId}
        );
      } else if (result.status === 'FAILED') {
        const message = result.error || 'Submission failed. Please try again.';
        setSubmitError(message);
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.CREATE_SIGNUP_SUBMIT_FAILED,
          {category: 'Create Signup', error: message}
        );
      }
    } catch (err) {
      const data = err.response && err.response.data;
      // 409 with an existing account = a duplicate. This is a hard block: the account already
      // exists and cannot be created again from here.
      if (
        err.response &&
        err.response.status === 409 &&
        data &&
        data.existingAccount
      ) {
        setSubmitConflict(data);
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.CREATE_SIGNUP_SUBMIT_FAILED,
          {category: 'Create Signup', error: 'duplicate_account'}
        );
      } else {
        const message =
          (data && data.message) ||
          err.message ||
          'Submission failed. Please try again.';
        setSubmitError(message);
        GoogleAnalyticsHelper.trackEvent(
          ANALYTICS_EVENTS.CREATE_SIGNUP_SUBMIT_FAILED,
          {category: 'Create Signup', error: message}
        );
      }
    } finally {
      setSubmitLoading(false);
      confirmingAccountRef.current = false;
    }
  }, [signupId, authHeaders]);

  const handleDeclineAccountCreation = useCallback(() => {
    setAccountCreationChoice('no');
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.CREATE_SIGNUP_ACCOUNT_CREATION_DECLINED,
      {category: 'Create Signup'}
    );
  }, []);

  const handleConfirmFollowUp = useCallback(() => {
    setFollowUpChoice('yes');
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.CREATE_SIGNUP_FOLLOW_UP_ACCEPTED,
      {category: 'Create Signup'}
    );
  }, []);

  const handleDeclineFollowUp = useCallback(() => {
    setFollowUpChoice('no');
    GoogleAnalyticsHelper.trackEvent(
      ANALYTICS_EVENTS.CREATE_SIGNUP_FOLLOW_UP_DECLINED,
      {category: 'Create Signup'}
    );
  }, []);

  useEffect(() => {
    if (currentStep === 3) {
      GoogleAnalyticsHelper.trackEvent(
        ANALYTICS_EVENTS.CREATE_SIGNUP_VIEW_PRICE_COMPARISON,
        {category: 'Create Signup'}
      );
    }
  }, [currentStep]);

  // ─── Create Another ───────────────────────────────────────────────────────────

  const handleCreateAnother = useCallback(() => {
    setSignupId(null);
    setPendingPhotos(prev => {
      prev.forEach(p => URL.revokeObjectURL(p.preview));
      return [];
    });
    setUploadedDocs(prev => {
      prev.forEach(d => d.preview && URL.revokeObjectURL(d.preview));
      return [];
    });
    setUploadError(null);
    setFulfillmentType(null);
    setFulfillmentTypeError(null);
    setEmail('');
    setConfirmEmail('');
    setMobile('');
    setEmployeeName('');
    setEmployeeId('');
    setEmailError(null);
    setConfirmEmailError(null);
    setMobileError(null);
    setContactError(null);
    setResolvedData(null);
    setBlockingMatches([]);
    setFieldSaving({});
    setFieldSaveErrors({});
    setProcessingStatus('idle');
    setProcessingError(null);
    setSubmitError(null);
    setSubmitConflict(null);
    setAccountCreationChoice(null);
    setFollowUpChoice(null);
    setCurrentStep(0);
    setPriceComparisonId(null);
    setPriceComparisonUnavailable(false);
    // A new signup (with a fresh referringEmployeeId) is created when the
    // rep next clicks "Next" on the Contact step — see handleContactNext.
  }, []);

  // ─── Render helpers ───────────────────────────────────────────────────────────

  const renderUploadStep = () => (
    <StepCard>
      {uploadLoading && (
        <LoadingOverlay>
          <Loading isLight={false} />
          <p>Uploading…</p>
        </LoadingOverlay>
      )}
      <SectionTitle>Upload Photo</SectionTitle>

      <FulfillmentChoice
        role="radiogroup"
        aria-labelledby="signup-fulfillment-type-label"
      >
        <FulfillmentChoiceLabel id="signup-fulfillment-type-label">
          Was this a delivery or a collection order?
        </FulfillmentChoiceLabel>
        <FulfillmentChoiceHint>
          This lets us compare against the right JJ price &mdash; we price
          delivery and collection differently, so it keeps the comparison fair.
        </FulfillmentChoiceHint>
        <FulfillmentOptions>
          <FulfillmentChoiceOption
            $selected={fulfillmentType === SERVICE_TYPE.DELIVERY}
          >
            <input
              type="radio"
              name="signupFulfillmentType"
              value={SERVICE_TYPE.DELIVERY}
              checked={fulfillmentType === SERVICE_TYPE.DELIVERY}
              onChange={() => {
                setFulfillmentType(SERVICE_TYPE.DELIVERY);
                setFulfillmentTypeError(null);
              }}
            />
            <div>
              <strong>Delivery</strong>
              <span>My supplier delivers to me</span>
            </div>
          </FulfillmentChoiceOption>
          <FulfillmentChoiceOption
            $selected={fulfillmentType === SERVICE_TYPE.COLLECTION}
          >
            <input
              type="radio"
              name="signupFulfillmentType"
              value={SERVICE_TYPE.COLLECTION}
              checked={fulfillmentType === SERVICE_TYPE.COLLECTION}
              onChange={() => {
                setFulfillmentType(SERVICE_TYPE.COLLECTION);
                setFulfillmentTypeError(null);
              }}
            />
            <div>
              <strong>Collection</strong>
              <span>I collect from my supplier / cash &amp; carry</span>
            </div>
          </FulfillmentChoiceOption>
        </FulfillmentOptions>
        {fulfillmentTypeError && (
          <FieldError>{fulfillmentTypeError}</FieldError>
        )}
      </FulfillmentChoice>

      <DropZone
        $isDragOver={isDragOver}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
      >
        <DropZoneLabel>🖼 Tap to upload a photo or document</DropZoneLabel>
        <DropZoneFormats>
          Supported formats: PDF, JPEG, PNG, WebP
        </DropZoneFormats>
        <input
          ref={fileInputRef}
          type="file"
          accept={INVOICE_UPLOAD_ACCEPTED_TYPES.join(',')}
          multiple
          onChange={handleFileInput}
          style={{display: 'none'}}
        />
        {/* Off-screen canvas used for capturing video frames */}
        <canvas ref={canvasRef} style={{display: 'none'}} />
      </DropZone>

      <ButtonRow style={{justifyContent: 'center', marginTop: '1.2rem'}}>
        <SecondaryButton onClick={openCamera}>Use Camera</SecondaryButton>
      </ButtonRow>

      {cameraPermission === 'denied' && (
        <ErrorMessage>
          Camera access was denied. Please allow camera access in your browser
          settings, then tap the button again.
        </ErrorMessage>
      )}
      {cameraPermission === 'requesting' && (
        <ErrorMessage
          style={{
            color: '#1976d2',
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25,118,210,0.06)',
          }}
        >
          Requesting camera access…
        </ErrorMessage>
      )}

      {pendingPhotos.length > 0 && (
        <PhotoGrid>
          {pendingPhotos.map((photo, i) => {
            const isPdf = photo.file.type === 'application/pdf';
            return (
              <PhotoCard key={i}>
                {isPdf ? (
                  <DocThumb>
                    <span>📄</span>
                    <span>{photo.file.name}</span>
                  </DocThumb>
                ) : (
                  <PhotoThumb
                    src={photo.preview}
                    alt={`Photo ${i + 1}`}
                    onClick={() => setPreviewPhoto(photo.preview)}
                    style={{cursor: 'pointer'}}
                  />
                )}
                <DeleteButton
                  onClick={() => removePendingPhoto(i)}
                  title="Remove photo"
                  disabled={uploadLoading}
                >
                  &times;
                </DeleteButton>
              </PhotoCard>
            );
          })}
        </PhotoGrid>
      )}

      {uploadedDocs.length > 0 && (
        <PhotoGrid>
          {uploadedDocs.map(doc => (
            <PhotoCard key={doc.id}>
              {doc.isPdf ? (
                <DocThumb>
                  <span>📄</span>
                  <span>{doc.name}</span>
                </DocThumb>
              ) : (
                <PhotoThumb
                  src={doc.preview}
                  alt={doc.name}
                  onClick={() => doc.preview && setPreviewPhoto(doc.preview)}
                  style={{cursor: doc.preview ? 'pointer' : 'default'}}
                />
              )}
              <DeleteButton
                onClick={() => handleDeleteDoc(doc.id, doc.name)}
                title="Remove photo"
                disabled={uploadLoading}
              >
                &times;
              </DeleteButton>
            </PhotoCard>
          ))}
        </PhotoGrid>
      )}

      {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}

      <ButtonRow>
        <SecondaryButton
          onClick={() => setCurrentStep(0)}
          disabled={uploadLoading}
        >
          Back
        </SecondaryButton>
        <PrimaryButton
          onClick={handleNextFromStep1}
          disabled={
            (pendingPhotos.length === 0 && uploadedDocs.length === 0) ||
            uploadLoading
          }
        >
          Start Processing
        </PrimaryButton>
      </ButtonRow>
    </StepCard>
  );

  const renderContactStep = () => {
    const isVerified = processingStatus === 'VERIFIED';

    // The customer-entered email/mobile are always used as the values submitted
    // for this signup. Once the document is verified, flag (don't block) if they
    // differ from what was extracted from the uploaded document.
    const extractedEmail =
      (resolvedData && resolvedData.email && resolvedData.email.value) || '';
    const extractedMobile =
      (resolvedData &&
        resolvedData.phoneNumber &&
        resolvedData.phoneNumber.value) ||
      '';
    const emailMismatch =
      isVerified &&
      !!extractedEmail &&
      email.trim().toLowerCase() !== extractedEmail.trim().toLowerCase();
    const mobileMismatch =
      isVerified &&
      !!extractedMobile &&
      mobile.replace(/\s+/g, '') !== extractedMobile.replace(/\s+/g, '');

    return (
      <StepCard>
        {savingContact && (
          <LoadingOverlay>
            <Loading isLight={false} />
            <p>Saving…</p>
          </LoadingOverlay>
        )}
        <SectionTitle>Contact Infomation</SectionTitle>
        {contactError && <ErrorMessage>{contactError}</ErrorMessage>}

        <FormGroup>
          <Label htmlFor="signup-email">
            Customer Email <span>(Obligatory)</span>
          </Label>
          <Input
            id="signup-email"
            type="email"
            value={email}
            onChange={e => {
              setEmail(e.target.value);
              setEmailError(null);
              setConfirmEmailError(null);
            }}
            placeholder="email@example.com"
            $hasError={!!emailError}
            disabled={savingContact}
          />
          {emailError && <FieldError>{emailError}</FieldError>}
          {!emailError && emailMismatch && (
            <FieldWarning>
              This doesn't match the email on the uploaded document (
              {extractedEmail}). The email entered here will be used.
            </FieldWarning>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="signup-confirm-email">
            Confirm Customer Email <span>(Obligatory)</span>
          </Label>
          <Input
            id="signup-confirm-email"
            type="email"
            value={confirmEmail}
            onChange={e => {
              setConfirmEmail(e.target.value);
              setConfirmEmailError(null);
            }}
            placeholder="Please confirm the email"
            $hasError={!!confirmEmailError}
            disabled={savingContact}
            onPaste={e => e.preventDefault()}
          />
          {confirmEmailError && <FieldError>{confirmEmailError}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="signup-mobile">
            Customer Mobile <span>(Obligatory)</span>
          </Label>
          <Input
            id="signup-mobile"
            type="tel"
            value={mobile}
            onChange={e => {
              setMobile(e.target.value);
              setMobileError(null);
            }}
            placeholder="+44 7700 000000"
            $hasError={!!mobileError}
            disabled={savingContact}
          />
          {mobileError && <FieldError>{mobileError}</FieldError>}
          {!mobileError && mobileMismatch && (
            <FieldWarning>
              This doesn't match the mobile number on the uploaded document (
              {extractedMobile}). The number entered here will be used.
            </FieldWarning>
          )}
        </FormGroup>

        {!isStaffAccount && (
          <>
            <FormGroup>
              <Label htmlFor="signup-employee-name">
                Employee Full Name <span>(optional)</span>
              </Label>
              <Input
                id="signup-employee-name"
                type="text"
                value={employeeName}
                onChange={e => setEmployeeName(e.target.value)}
                placeholder="Employee full name"
                disabled={savingContact}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="signup-employee-id">
                Employee ID <span>(optional)</span>
              </Label>
              <Input
                id="signup-employee-id"
                type="text"
                value={employeeId}
                onChange={e => setEmployeeId(e.target.value)}
                placeholder="Employee ID"
                disabled={savingContact}
              />
            </FormGroup>
          </>
        )}

        <ButtonRow>
          <PrimaryButton onClick={handleContactNext} disabled={savingContact}>
            Next
          </PrimaryButton>
        </ButtonRow>
      </StepCard>
    );
  };

  // Shown alongside the price comparison so the rep sees the extracted
  // customer details and the savings from the same document together.
  const renderExtractedCustomerData = () =>
    resolvedData && (
      <>
        <SectionTitle style={{marginTop: '2.4rem'}}>
          Extracted Business Data
        </SectionTitle>
        <ResolvedFieldsGrid>
          {CUSTOMER_FIELDS.map(name => (
            <ResolvedField
              key={name}
              fieldName={name}
              fieldData={resolvedData[name]}
              onSave={handleFieldSave}
              saving={!!fieldSaving[name]}
              saveError={fieldSaveErrors[name]}
              canEdit={isStaffAccount}
            />
          ))}
        </ResolvedFieldsGrid>
      </>
    );

  // Always shown on the Price Comparison step — a "preparing" or fallback
  // message if the comparison isn't ready yet, rather than the section just
  // being absent.
  const renderPriceComparisonSection = () => (
    <>
      <SectionTitle style={{marginTop: '3.2rem'}}>
        Price Comparison
      </SectionTitle>
      {priceComparisonId ? (
        <PriceComparisonPanel
          id={priceComparisonId}
          history={history}
          isReferral
        />
      ) : priceComparisonUnavailable ? (
        <p style={{fontSize: '1.3rem', color: '#555'}}>
          We couldn&apos;t start a price comparison for this document.
        </p>
      ) : (
        <p style={{fontSize: '1.3rem', color: '#555'}}>
          Preparing your price comparison…
        </p>
      )}
    </>
  );

  // Confirm Details step — shows the extraction/verification progress, then
  // the extracted business data with the confirm action. The price
  // comparison only starts once the user confirms and moves to the next
  // step (see renderPriceComparisonStep below).
  const renderConfirmDetailsStep = () => {
    if (processingStatus === 'FAILED') {
      return (
        <StepCard>
          <ProcessingWrapper>
            <StatusBadge $status="FAILED">FAILED</StatusBadge>
            <ProcessingTitle style={{marginTop: '1.6rem'}}>
              Verification Failed
            </ProcessingTitle>
            <ProcessingStatusText>
              {processingError || 'The document could not be verified.'}
            </ProcessingStatusText>
          </ProcessingWrapper>
          <ButtonRow style={{justifyContent: 'center'}}>
            <SecondaryButton onClick={() => setCurrentStep(1)}>
              Back to Documents
            </SecondaryButton>
          </ButtonRow>
        </StepCard>
      );
    }

    if (
      processingStatus !== 'VERIFIED' &&
      processingStatus !== 'ACCOUNT_CREATED'
    ) {
      // Still extracting/verifying (or any other intermediate status) — the
      // price comparison is already running in parallel, so show it here too
      // rather than waiting for verification to finish.
      const statusLabel =
        INTERMEDIATE_STATUS_LABELS[processingStatus] || 'Processing…';

      return (
        <StepCard>
          <ProcessingWrapper>
            <ProcessingTitle>Processing Your Signup</ProcessingTitle>
            <SpinnerWrapper>
              <Loading isLight={false} />
            </SpinnerWrapper>
            <ProcessingStatusText>{statusLabel}</ProcessingStatusText>
            {processingStatus && processingStatus !== 'polling' && (
              <StatusBadge
                $status={processingStatus}
                style={{marginTop: '1.2rem'}}
              >
                {processingStatus.replace(/_/g, ' ')}
              </StatusBadge>
            )}
          </ProcessingWrapper>
          {processingError && (
            <ErrorMessage style={{textAlign: 'center'}}>
              {processingError}
            </ErrorMessage>
          )}
        </StepCard>
      );
    }

    if (processingStatus === 'VERIFIED') {
      return (
        <StepCard>
          <SectionHint>
            Double check and edit any missing/incorrect information. Make sure
            information is correct, especially the email.
          </SectionHint>
          {renderExtractedCustomerData()}

          <ButtonRow>
            <SecondaryButton
              onClick={() => setCurrentStep(1)}
              disabled={submitLoading}
            >
              Back to Upload Photo
            </SecondaryButton>
            <PrimaryButton
              onClick={handleContinueToFinalStep}
              disabled={submitLoading}
            >
              {submitLoading ? 'Continuing…' : 'Continue'}
            </PrimaryButton>
          </ButtonRow>
        </StepCard>
      );
    }

    // ACCOUNT_CREATED — by this point confirming has already advanced
    // currentStep to 3, so this step no longer renders. Kept as a defensive
    // fallback rather than assuming that transition always wins the race.
    return null;
  };

  // Confirming the registration doesn't block navigation here — this card
  // reflects whatever state that background call is currently in, while the
  // price comparison below always runs regardless of its outcome.
  // Staff-only — non-staff never call handleConfirmAccountCreation (see
  // refer-a-business-openapi.yaml: only staff/admin may submit), so
  // submitLoading/submitConflict/submitError stay unset for them and they
  // never reach this card at all (renderPriceComparisonStep only calls this
  // for staff).
  const renderRegistrationStatusCard = () => {
    if (submitLoading) {
      return (
        <StepCard>
          <ProcessingWrapper>
            <ProcessingTitle>Sending information for review</ProcessingTitle>
            <SpinnerWrapper>
              <Loading isLight={false} />
            </SpinnerWrapper>
            <ProcessingStatusText>
              This will just take a moment…
            </ProcessingStatusText>
          </ProcessingWrapper>
        </StepCard>
      );
    }

    // Known upfront from the duplicate check on the Confirm Details step, or
    // (rarely) discovered only now via a 409 from submitSignup if a matching
    // account was created elsewhere in between.
    const existingAccountMatches =
      blockingMatches.length > 0
        ? blockingMatches
        : submitConflict && submitConflict.existingAccount
        ? [submitConflict.existingAccount]
        : [];

    if (existingAccountMatches.length > 0) {
      return (
        <StepCard>
          <DuplicateWarning>
            <h3>⚠ This business is already a customer</h3>
            <p>
              {(submitConflict && submitConflict.message) ||
                'An existing account matches this business, so a new account cannot be created.'}
            </p>
            {existingAccountMatches.map(m => (
              <DuplicateMatch key={m.tradingAccountId}>
                <strong>
                  {m.businessName || m.tradingName || 'Existing account'}
                </strong>
                <span>
                  {m.tradingAccountId}
                  {m.postcode ? ` · ${m.postcode}` : ''}
                </span>
                {m.matchedOn && m.matchedOn.length > 0 && (
                  <em>
                    Matched on:{' '}
                    {m.matchedOn
                      .map(f => SIGNUP_MATCHED_ON_LABELS[f] || f)
                      .join(', ')}
                  </em>
                )}
              </DuplicateMatch>
            ))}
            <p style={{marginTop: '0.8rem', marginBottom: 0}}>
              To add this customer to the existing account, please contact the
              office.
            </p>
          </DuplicateWarning>

          {followUpChoice === null && (
            <ProcessingWrapper style={{padding: '1.6rem 0 0'}}>
              <ProcessingStatusText>
                Would you like us to contact you to discuss this further?
              </ProcessingStatusText>
              <ButtonRow
                style={{justifyContent: 'center', marginTop: '1.2rem'}}
              >
                <SecondaryButton onClick={handleDeclineFollowUp}>
                  No
                </SecondaryButton>
                <PrimaryButton onClick={handleConfirmFollowUp}>
                  Yes
                </PrimaryButton>
              </ButtonRow>
            </ProcessingWrapper>
          )}
          {followUpChoice === 'yes' && (
            <ProcessingWrapper style={{padding: '1.6rem 0 0'}}>
              <ProcessingStatusText>
                Thank you. Our team will be in touch to discuss this further.
              </ProcessingStatusText>
            </ProcessingWrapper>
          )}
          {followUpChoice === 'no' && (
            <ProcessingWrapper style={{padding: '1.6rem 0 0'}}>
              <ProcessingStatusText>Thank you.</ProcessingStatusText>
            </ProcessingWrapper>
          )}

          <ButtonRow style={{justifyContent: 'center', marginTop: '1.2rem'}}>
            <PrimaryButton onClick={handleCreateAnother}>
              Create Another Signup
            </PrimaryButton>
          </ButtonRow>
        </StepCard>
      );
    }

    if (submitError) {
      return (
        <StepCard>
          <ErrorMessage>{submitError}</ErrorMessage>
          <ButtonRow style={{justifyContent: 'center'}}>
            <SecondaryButton onClick={() => setCurrentStep(2)}>
              Back to Confirm Details
            </SecondaryButton>
          </ButtonRow>
        </StepCard>
      );
    }

    if (accountCreationChoice === null) {
      return (
        <StepCard>
          <ProcessingWrapper>
            <ProcessingTitle>Account ready to be created</ProcessingTitle>
            <ProcessingStatusText>
              Would you like us to create your account?
            </ProcessingStatusText>
            <ButtonRow style={{justifyContent: 'center', marginTop: '1.6rem'}}>
              <SecondaryButton
                onClick={handleDeclineAccountCreation}
                disabled={submitLoading}
              >
                No
              </SecondaryButton>
              <PrimaryButton
                onClick={handleConfirmAccountCreation}
                disabled={submitLoading}
              >
                Yes
              </PrimaryButton>
            </ButtonRow>
          </ProcessingWrapper>
        </StepCard>
      );
    }

    return (
      <SuccessCard>
        <h2>Thank you.</h2>
        {accountCreationChoice === 'yes' && (
          <p>
            Our team is reviewing the information and will be in touch shortly.
          </p>
        )}
        <ButtonRow style={{justifyContent: 'center', marginTop: '2.4rem'}}>
          <PrimaryButton onClick={handleCreateAnother}>
            Create Another Signup
          </PrimaryButton>
        </ButtonRow>
      </SuccessCard>
    );
  };

  // Price Comparison step. Staff confirm manually via the Confirm Details
  // step and land here already submitting/submitted, so their registration
  // status card shows alongside the comparison. Non-staff can't submit at
  // all (see refer-a-business-openapi.yaml) and skip straight here from
  // Upload Photo — just the comparison (already processing in the
  // background since upload) plus the possible-existing-account notice.
  const renderPriceComparisonStep = () => (
    <>
      {isStaffAccount
        ? renderRegistrationStatusCard()
        : renderPossibleExistingAccountNotice()}
      {renderPriceComparisonSection()}
    </>
  );

  // ─── Pre-signup screens ───────────────────────────────────────────────────────
  const pageTitle = 'New Customer Registration';
  if (!isLoggedIn) {
    return (
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <CenteredSection>
          <p>Please log in to create a new signup.</p>
        </CenteredSection>
      </PageWrapper>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────────

  return (
    <>
      {cameraOpen && (
        <CameraOverlay>
          <VideoPreview
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={cameraMirrored ? {transform: 'scaleX(-1)'} : undefined}
          />
          <CameraControls>
            <SecondaryButton
              onClick={() => setCameraMirrored(m => !m)}
              style={{color: '#fff', borderColor: 'rgba(255,255,255,0.5)'}}
              title="Mirror image"
            >
              {cameraMirrored ? '↔ Mirrored' : '↔ Mirror'}
            </SecondaryButton>
            <CaptureButton onClick={capturePhoto} title="Capture photo" />
            <SecondaryButton
              onClick={closeCamera}
              style={{color: '#fff', borderColor: 'rgba(255,255,255,0.5)'}}
            >
              Cancel
            </SecondaryButton>
          </CameraControls>
        </CameraOverlay>
      )}
      {previewPhoto && (
        <PhotoPreviewOverlay onClick={() => setPreviewPhoto(null)}>
          <img src={previewPhoto} alt="Preview" />
        </PhotoPreviewOverlay>
      )}
      <PageWrapper>
        <PageTitle>{pageTitle}</PageTitle>
        <StepIndicator
          // Non-staff skip currentStep 2 entirely (see handleNextFromStep1) and jump
          // straight to 3, but their label set only has 3 entries (no "Confirm Details") —
          // map that onto the last one so the indicator still shows the right step as current.
          currentStep={isStaffAccount ? currentStep : Math.min(currentStep, 2)}
          labels={
            isStaffAccount
              ? SIGNUP_STAFF_STEP_LABELS
              : SIGNUP_NON_STAFF_STEP_LABELS
          }
        />
        {currentStep === 0 && renderContactStep()}
        {currentStep === 1 && renderUploadStep()}
        {currentStep === 2 && renderConfirmDetailsStep()}
        {currentStep === 3 && renderPriceComparisonStep()}
      </PageWrapper>
    </>
  );
};

CreateSignupPage.propTypes = {
  history: PropTypes.object.isRequired,
};

export default CreateSignupPage;
